import { LocalDate } from "@js-joda/core";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { Text } from "components/designV2/text/text";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import type { CompanyBankAccountType } from "model/Company";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  HeaderRightAligned,
  IndividualSalaryFilters,
  PeriodNavigatorContainer,
  SalaryPeriodCard,
  SalaryPageContainer,
  SalaryEmptyState,
} from "./salary.styled";
import { ColorV2 } from "config/colors";
import { Api } from "services/api/api.service";
import { FileViewer } from "../fileViewer/fileViewer.component";
import { AGIList } from "./agiList.component";
import { SalaryPayoutPanel } from "./salaryPayoutPanel.component";
import { useSalaryPayoutPanel } from "./useSalaryPayoutPanel";

const getCompanySettingValue = (
  settings: Array<{ key: string; value: string }> | undefined,
  key: string,
): string => {
  return settings?.find((setting) => setting.key === key)?.value ?? "";
};

export const AGIOverview = (): JSX.Element => {
  const [currentDate, setCurrentDate] = useState(
    LocalDate.now().minusMonths(1),
  );
  const [agiRefreshKey, setAgiRefreshKey] = useState(0);
  const [isCreatingAGI, setIsCreatingAGI] = useState(false);
  const [createAGIError, setCreateAGIError] = useState(false);
  const [companyState] = useCompanyStore();
  const company = companyState.company;
  const { t } = useTranslation();
  const salaryPayoutPanel = useSalaryPayoutPanel({
    companyId: company?.id,
    currentDate,
  });
  const bankAgreementId = getCompanySettingValue(
    companyState.companySettings,
    "CompanyBankAgreementId",
  );
  const bankAccountValue = getCompanySettingValue(
    companyState.companySettings,
    "CompanyBankAccountValue",
  );
  const bankAccountTypeValue = getCompanySettingValue(
    companyState.companySettings,
    "CompanyBankAccountType",
  );
  const bankAccountType: CompanyBankAccountType =
    bankAccountTypeValue === "InternationalBankAccount"
      ? "InternationalBankAccount"
      : "BankAccount";

  const fetchAGIFile = useCallback(() => {
    if (!company) {
      return Promise.reject(new Error("Missing company"));
    }

    return Api().company.salary.getAGI(
      company.id,
      currentDate.year(),
      currentDate.monthValue(),
    );
  }, [company, currentDate]);

  const handlePeriodChange = (nextDate: LocalDate): void => {
    setCreateAGIError(false);
    setCurrentDate(nextDate);
  };

  const createAGI = async (): Promise<void> => {
    if (!company || isCreatingAGI) return;

    setIsCreatingAGI(true);
    setCreateAGIError(false);
    try {
      await Api().company.salary.createAGI({
        companyId: company.id,
        period: currentDate,
      });
      setAgiRefreshKey((currentKey) => currentKey + 1);
    } catch {
      setCreateAGIError(true);
    } finally {
      setIsCreatingAGI(false);
    }
  };

  return (
    <SalaryPageContainer>
      <HeaderRightAligned>
        <TooltipHover text={t("Salary.AGIInfo")} arrow="up">
          <Text typography="s1" color={ColorV2.Blue1}>
            {t("Salary.WhatIs", { name: t("Salary.AGI").toLowerCase() })}
          </Text>
        </TooltipHover>
      </HeaderRightAligned>
      <SalaryPeriodCard>
        <IndividualSalaryFilters>
          <PeriodNavigatorContainer>
            <PeriodNavigator
              currentDate={currentDate}
              mode="month"
              onChange={handlePeriodChange}
            />
          </PeriodNavigatorContainer>
        </IndividualSalaryFilters>
      </SalaryPeriodCard>

      {company && (
        <FileViewer
          fetchEndpoint={fetchAGIFile}
          filename={`AGI_${currentDate.year()}_${currentDate.monthValue()}.xml`}
          type="application/xml"
          currentDate={currentDate}
          refreshKey={agiRefreshKey}
          emptyState={
            <SalaryEmptyState>
              <Text typography="b2" color={ColorV2.Blue1} align="center">
                {t("Salary.NoSalaryBasisForPeriod")}
              </Text>
              <Text typography="s1" color={ColorV2.Blue3} align="center">
                {t("Salary.SalaryBasisAvailability")}
              </Text>
              {createAGIError && (
                <Text
                  typography="s1"
                  color={ColorV2.NegativeRed}
                  align="center"
                >
                  {t("Salary.CreateSalaryBasisError")}
                </Text>
              )}
              <PrimaryButton
                label={t("Salary.CreateSalaryBasis")}
                isLoading={isCreatingAGI}
                onClick={createAGI}
              />
            </SalaryEmptyState>
          }
          extraActions={
            <PrimaryButton
              label={t("Salary.PayOutSalaries")}
              size="small"
              onClick={salaryPayoutPanel.openPanel}
            />
          }
          ListComponent={AGIList}
        />
      )}
      <SalaryPayoutPanel
        bankAgreementId={bankAgreementId}
        bankAccountType={bankAccountType}
        bankAccountValue={bankAccountValue}
        canCreatePayoutFile={salaryPayoutPanel.canCreatePayoutFile}
        currentDate={currentDate}
        excludedWorkerCount={salaryPayoutPanel.excludedWorkerCount}
        isCreatingPayoutFile={salaryPayoutPanel.isCreatingPayoutFile}
        isOpen={salaryPayoutPanel.isOpen}
        payoutDate={salaryPayoutPanel.payoutDate}
        payoutWorkerCount={salaryPayoutPanel.payoutWorkerCount}
        previewState={salaryPayoutPanel.previewState}
        totalNetPayoutAmount={salaryPayoutPanel.totalNetPayoutAmount}
        onClose={salaryPayoutPanel.closePanel}
        onCreate={salaryPayoutPanel.createPayoutFile}
        onPayoutDateChange={salaryPayoutPanel.setPayoutDate}
      />
    </SalaryPageContainer>
  );
};
